import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export interface SignupRiskRow {
  user_id: string;
  email_domain: string;
  created_at: string;
  network_siblings: number;
  uploads: number;
  credits_spent: number;
  duplicate_matches: number;
  free_grant_withheld: boolean;
  domain_blocked: boolean;
}

export interface InboxCluster {
  canonical: string;
  accounts: number;
  emails: string[];
  last_signup: string;
  uploads: number;
}

export function isRisky(r: SignupRiskRow): boolean {
  return r.network_siblings >= 1 || r.duplicate_matches > 0 || r.free_grant_withheld || r.domain_blocked;
}

export function summarise(rows: SignupRiskRow[], now = Date.now()) {
  const day = rows.filter((r) => now - new Date(r.created_at).getTime() < 86_400_000);
  return {
    newAccounts: day.length,
    sharedNetwork: day.filter((r) => r.network_siblings >= 2).length,
    duplicates: day.filter((r) => r.duplicate_matches > 0).length,
    creditsSpent: Math.round(day.reduce((s, r) => s + Number(r.credits_spent || 0), 0) * 10) / 10,
  };
}

const SignupRiskOverview: React.FC = () => {
  const [rows, setRows] = useState<SignupRiskRow[] | null>(null);
  const [clusters, setClusters] = useState<InboxCluster[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const client = supabase as any;
    const { data, error: e } = await client.rpc('admin_signup_risk_overview', { _days: 14 });
    if (e) setError(e.message);
    else setRows((data ?? []) as SignupRiskRow[]);
    const res = await client.rpc('admin_inbox_clusters', { _days: 30 });
    if (res && !res.error) setClusters((res.data ?? []) as InboxCluster[]);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-destructive">Could not load signup risk: {error}</p>;
  if (!rows) return <Skeleton className="h-40 w-full" />;

  const s = summarise(rows);
  const alert = s.sharedNetwork > 0 || s.duplicates > 0;

  return (
    <div className="space-y-4">
      <Card className={alert ? 'border-destructive/50' : undefined}>
        <CardContent className="py-4 text-sm">
          Last 24 hours: {s.newAccounts} new accounts, {s.creditsSpent} credits spent by them,{' '}
          {s.sharedNetwork} from a network with 2 or more other new accounts, {s.duplicates} with a manuscript
          also on another account.
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">New accounts, last 14 days</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No new accounts.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-4">Signed up</th>
                  <th className="py-2 pr-4">Domain</th>
                  <th className="py-2 pr-4">Same network</th>
                  <th className="py-2 pr-4">Uploads</th>
                  <th className="py-2 pr-4">Credits spent</th>
                  <th className="py-2 pr-4">Signals</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className={isRisky(r) ? 'bg-destructive/5' : undefined}>
                    <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-4">{r.email_domain}</td>
                    <td className="py-2 pr-4">{r.network_siblings}</td>
                    <td className="py-2 pr-4">{r.uploads}</td>
                    <td className="py-2 pr-4">{Number(r.credits_spent).toFixed(1)}</td>
                    <td className="py-2 pr-4 space-x-1">
                      {r.domain_blocked && <Badge variant="destructive">Blocked domain</Badge>}
                      {r.free_grant_withheld && <Badge variant="destructive">No free credits</Badge>}
                      {r.duplicate_matches > 0 && <Badge variant="secondary">Duplicate manuscript</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Several accounts on one inbox</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {!clusters || clusters.length === 0 ? (
            <p className="text-sm text-muted-foreground">No shared inboxes in the last 30 days.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-4">Inbox</th>
                  <th className="py-2 pr-4">Accounts</th>
                  <th className="py-2 pr-4">Uploads</th>
                  <th className="py-2 pr-4">Last signup</th>
                </tr>
              </thead>
              <tbody>
                {clusters.map((c) => (
                  <tr key={c.canonical}>
                    <td className="py-2 pr-4" title={c.emails.join(', ')}>{c.canonical}</td>
                    <td className="py-2 pr-4">{c.accounts}</td>
                    <td className="py-2 pr-4">{c.uploads}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{new Date(c.last_signup).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupRiskOverview;
